import { useEffect, useState } from 'react';
import { onSnapshot, type DocumentReference, type Query } from 'firebase/firestore';

export type WithId<T> = T & { id: string; pending?: boolean };

interface State<T> {
  data: T;
  loading: boolean;
  error: Error | null;
}

/**
 * Subscribe to a query. `key` must change whenever the query changes
 * (queries are not referentially stable). Results are tagged with the key they
 * belong to, so a key change shows loading without a synchronous reset.
 */
export function useCollection<T>(query: Query | null, key: string): State<WithId<T>[]> {
  const [snap, setSnap] = useState<{ key: string; data: WithId<T>[]; error: Error | null } | null>(
    null,
  );

  useEffect(() => {
    if (!query) return;
    return onSnapshot(
      query,
      { includeMetadataChanges: true },
      (s) =>
        setSnap({
          key,
          error: null,
          data: s.docs.map((d) => ({
            ...(d.data({ serverTimestamps: 'estimate' }) as T),
            id: d.id,
            pending: d.metadata.hasPendingWrites,
          })),
        }),
      (error) => setSnap({ key, data: [], error }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, !!query]);

  if (!query) return { data: [], loading: false, error: null };
  const fresh = snap?.key === key;
  return { data: fresh ? snap.data : [], loading: !fresh, error: fresh ? snap.error : null };
}

export function useDocument<T>(
  ref: DocumentReference | null,
  key: string,
): State<WithId<T> | null> & { exists: boolean } {
  const [snap, setSnap] = useState<{
    key: string;
    data: WithId<T> | null;
    error: Error | null;
  } | null>(null);

  useEffect(() => {
    if (!ref) return;
    return onSnapshot(
      ref,
      (s) =>
        setSnap({
          key,
          error: null,
          data: s.exists()
            ? { ...(s.data({ serverTimestamps: 'estimate' }) as T), id: s.id }
            : null,
        }),
      (error) => setSnap({ key, data: null, error }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, !!ref]);

  if (!ref) return { data: null, loading: false, error: null, exists: false };
  const fresh = snap?.key === key;
  const data = fresh ? snap.data : null;
  return { data, loading: !fresh, error: fresh ? snap.error : null, exists: !!data };
}
